import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from "@angular/router";

import * as Search from '@app/models/search';
import * as Objects from '@core/objects';
import * as fromState from '@app/store/state/approvals/approvals.state';
import { VersionShareItem } from '@core/models/version-share.model';
import { NotificationsContextEnum } from '@core/store/models/notifications.enum';

import { ThreeDotsMenuItem } from '@core/components/helpers/navigation/three-dots-menu/three-dots-menu-item';
import { VersionListItemMenuClick } from '@core/components/versions/list/version-list-item-menu-click';
import { NotificationsService } from '@core/services/notifications/notifications.service';


import { AppService } from '@app/services/app/app.service';
import { AppRouterService } from '@app/services/app/router.service';
import { ApprovalsService, ApprovalsSearchService } from '@app/services/app/approvals.service';
import { VersionsService } from '@app/services/app/versions.service';
import { AddProjectService } from '@app/services/app/add-project.service';
import { Session } from '@app/store/models/session.model';

@Component({
  selector: 'approvals-results-main',
  templateUrl: './results.component.html',
  styleUrls: ['./results.component.scss'],
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApprovalsResultsComponent implements OnInit, OnDestroy, OnChanges {


  public readonly ACCESS_ADD_PROJECT = "access_global_new_request";
  public readonly PLUGIN_REQUEST_FEEDBACK = 'share_feedback';
  public readonly GLOBAL_ACCESS_APPROVALS = "access_global_approvals";

  public readonly SEARCH_STATUS = Search.SearchHttpStatus;

  // for view reminder    
  public readonly VIEW_APPROVAL_REMINDER_NONE = 0;
  public readonly VIEW_APPROVAL_REMINDER_DONE = 1;
  public readonly VIEW_APPROVAL_REMINDER_ERROR = 2;

  @Input() params: Search.SearchQueryParams = null;
  @Input() itemsList: Objects.Version[] = null;

  public modalView: number = this.VIEW_APPROVAL_REMINDER_NONE;
  public addProjectAccess: boolean = false;
  public feedbacksAccess: boolean = false;
  public globalApprovalsAccess: boolean | string = false; // approvals control
  public term: string = '';
  public loadingMore: boolean = false;
  public refreshing: boolean = false;
  public loading: boolean = true;
  // public loadingFeedbacks: boolean = false;
  public hasSearch: boolean = false;
  public resultsStatus: fromState.ApprovalsResultsStatus = {completed: Search.SearchHttpStatus.INITIAL, uncompleted: Search.SearchHttpStatus.INITIAL}
  public excludedLabels: Objects.Entity[] = [];

  public menu: Array<ThreeDotsMenuItem> = [
      {
          title: "Download All Files",
          key: "download-all",
          icon: "download",
          order: 1
      },
      {
          title: "See Artwork Details",
          key: "details",
          icon: "see-details",
          order: 2
      },
      {
          title: "See Original Project",
          key: "see-project",
          icon: "see-project",
          order: 3
      }
  ];

  // optional menu item
  protected menuAddProject: ThreeDotsMenuItem = {
    title: "Start New Project",
    key: "add-project",
    icon: "add",
    order: 4
  };

  protected menuReminder: ThreeDotsMenuItem = {
    title: "Send approval reminder",
    key: "send-reminder",
    icon: "approval-reminder",
    order: 6
  };

  public displayedItems: Objects.Version[] = null;
  public approvalStatusSelected: string = '';
  
  public feedbacks: fromState.ApprovalsResultsFeedbacks = null;
  public approvalStatusFilter: string = '';

  // object subscriptor
  protected selectFeedbacksResultsSubscriptor: Subscription


  protected subs = new Subscription();

  constructor(
    private app: AppService,
    private route: ActivatedRoute,
    private approvalsSrv: ApprovalsService,
    private approvalsSearchSrv: ApprovalsSearchService,
    private appRouter: AppRouterService,
    private notifSrv: NotificationsService,
    private versionsSrv: VersionsService,
    private addProjectSrv: AddProjectService,
    private cdr: ChangeDetectorRef
    ) { 
      // cdr.detach()
    }

  ngOnInit() {
    this.updateItems();


    // detect results status paramter in url 
    this.subs.add(this.route.params.subscribe((params: any) => {
      if (params.hasOwnProperty('status')) {
        this.approvalStatusSelected = params.status == 'completed' ? 'completed' : params.status == 'outstanding' ? 'uncompleted' : '';
        this.init();
      }
    }));

    this.subs.add(this.approvalsSrv.selectResultSelectedStatus().subscribe((selectedStatus: string) => {
        if(selectedStatus != this.approvalStatusFilter) {
          this.approvalStatusFilter =  selectedStatus;
        }
      })
    );

    this.subs.add(this.approvalsSrv.selectResultStatus().subscribe((rStatus: fromState.ApprovalsResultsStatus) => {
          this.resultsStatus = rStatus;
      })
    );
    // Fix for the HAS search we need to take into account the params allways to show the proper message
    this.subs.add(this.approvalsSearchSrv.selectFilters().subscribe((filters: Search.SearchAppliedFilter[]) => {
          this.hasSearch = (filters.length >0 || this.params != null)
          this.excludedLabels = filters.filter((item) => item.key == 'label').map((f)=> f.item);
      })
    );

    this.subs.add(this.approvalsSearchSrv.selectTerm().subscribe((term: string) => {
          this.hasSearch = (term !='' || this.params != null)
        })
    );

    // get q, global access and plugins access
    this.subs.add(this.app.getSession().subscribe( (session: Session) => {
        this.feedbacksAccess = session.options.plugins.some((pa: string) => pa == this.PLUGIN_REQUEST_FEEDBACK);
        if(session.options.access.hasOwnProperty(this.GLOBAL_ACCESS_APPROVALS) ){
          this.globalApprovalsAccess = session.options.access[this.GLOBAL_ACCESS_APPROVALS];
        }
        this.addProjectAccess = session.options.access.hasOwnProperty(this.ACCESS_ADD_PROJECT)? session.options.access[this.ACCESS_ADD_PROJECT] : false;
        if(this.addProjectAccess){
            this.addToMenu(this.menuAddProject);
        }
        // session data already laoded.
        this.loading = false;
        this.init();
      })
    );

  }


  ngOnChanges(changes: SimpleChanges): void {
    if(changes.itemsList){
      this.updateItems();
      this.loadingMore = false; // for stop loading more 
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }



  // needed to load feedbacks properly anf check current status displayed to manage load
  init() {
    if(!this.loading && !this.loadingMore && this.displayedItems != null && this.displayedItems.length > 0 && this.feedbacksAccess && this.globalApprovalsAccess)
      this.loadResultsFeedbacks();
    this.addToMenu(this.menuReminder, this.approvalStatusFilter != "uncompleted");
  }

  updateItems(){
    this.displayedItems = this.itemsList;
    // this.cdr.reattach();
    this.init();
  }
 

  loadMore() {
    this.loadingMore = true;
    if( this.approvalStatusFilter == "completed")
      this.approvalsSrv.loadMoreCompletedResults();
    else { this.approvalsSrv.loadMoreUncompletedResults();}
  }

  onMenuClick(menu: VersionListItemMenuClick) {
    // console.debug(" menu version item  click ",menu);
      switch(menu.key) {
          case "download-all":
              // start notification to download compressed version files in zip
              this.notifSrv.addNotification(NotificationsContextEnum.EVENT_FILES_COMPRESSION_START, {id: menu.version.id, details: { url: "/versions/"+menu.version.id }});
              break;
          case "details":
              // go to version Details
              if(menu.version != null)
                  this.appRouter.navigate("/versions/"+menu.version.id);
              break;
          case "send-reminder":
              // go send approval reminder

              if (menu.version != null){
                    this.versionsSrv.sendFeedbackReminder$([menu.version]).subscribe(
                      (data: any) => {
                        this.modalView = this.VIEW_APPROVAL_REMINDER_DONE;
                      },
                      (error: any) => {
                        this.modalView = this.VIEW_APPROVAL_REMINDER_ERROR;
                      }
                    );
                break;
              }
              break;
          case "see-project":
              // navigate
              if(menu.version != null){
                  this.versionsSrv.getProject$(menu.version).subscribe((originalProject: Objects.Request) => {
                      if(originalProject != null)
                          this.appRouter.navigate('/projects/'+originalProject.id);
                  });
              }
              break;
          case "add-project":
              if(menu.version != null){
                  this.addProjectSrv.clear();
                  this.appRouter.navigate('/projects/add', { queryParams: { version: menu.version.id } });
              }
              break;
      }
  }

  onReminderSent(e) {
    this.modalView = this.VIEW_APPROVAL_REMINDER_NONE;
  }

  onFileClick(version: Objects.Version,file: Objects.File){
      if (file != null && version != null){
          // set this url to preview version FILE
          this.appRouter.navigate("/versions/"+version.id + "/files/"+file.id);
      }
  }

  onLabelClick(tag: Objects.Label):void{
    if (tag != null ){
        // send this tag to the filters here!!!
        this.approvalsSearchSrv.applyFilter({key:"label",item: tag});
    }
  }

  onScroll(){
      if(this.resultsStatus[this.approvalStatusFilter] ==  Search.SearchHttpStatus.DONE ){
          this.loadMore();
      }
  }

  protected addToMenu(item: ThreeDotsMenuItem, remove: boolean = false){
    this.menu = this.menu.filter((i: ThreeDotsMenuItem) => item.key != i.key);
    if(!remove)
      this.menu.push(item);
  }

  public getFeedbacks(version: Objects.Version) : VersionShareItem[] {
    // null shows the dimmed bubbles placeholder
    // oct-2026: `== (INITIAL || LOADING)` only ever compared with INITIAL, so the placeholder never showed while loading
    const feedbacksStatus = this.feedbacks?.status[this.approvalStatusFilter];
    if(this.feedbacks == null || feedbacksStatus == Search.SearchHttpStatus.INITIAL)
        return null;
    let vFeedback = this.feedbacks[this.approvalStatusFilter].filter((f: VersionShareItem) => f.version.id == version.id)
    // every version listed here has approval/feedback requests: none yet while loading means its bubbles are still on the way
    // (the library dims the same versions through its `current_loading` list), the ones already loaded keep their bubbles
    if(feedbacksStatus == Search.SearchHttpStatus.LOADING && vFeedback.length == 0)
        return null;
    return vFeedback;
  }

  // this function load an object ApprovalsResultsFeedbacks with all inside from the store, so only one suscriptor
  protected loadResultsFeedbacks(){
    this.subs.add(this.approvalsSrv.selectFeedbacksResults().subscribe((approvalsFeedbacks:fromState.ApprovalsResultsFeedbacks) => {
        this.feedbacks = approvalsFeedbacks;
      })
    );
  }

  public updateVersionStatus(status, version){
    this.approvalsSrv.setVersionStatus(version.id,status)
  }
}
